
/**
 * Utility functions for SearchAtlas OTTO SEO integration
 * Enhanced for SSR compatibility
 */

/**
 * Notify OTTO SEO that content has been updated
 * This function should be called after dynamic content loads
 */
export const notifyOttoContentUpdate = (data: {
  path: string;
  title: string;
  description: string;
  type?: 'website' | 'article' | 'product';
  imageUrl?: string;
}) => {
  try {
    // Update meta tags before the event for immediate effect
    updateMetaTags({
      title: data.title,
      description: data.description,
      type: data.type,
      imageUrl: data.imageUrl
    });
    
    // Check for browser environment before using DOM APIs
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Trigger a custom event for OTTO to detect
      const ottoEvent = new CustomEvent('ottoContentUpdated', {
        detail: data
      });
      document.dispatchEvent(ottoEvent);
      
      // Let SearchAtlas know about the content update (if their script is loaded)
      if (window.sa_event) {
        window.sa_event('contentUpdate', data);
      }
      
      console.log('OTTO SEO: Content update notified', data.path);
    }
  } catch (error) {
    console.warn('Error notifying OTTO of content update:', error);
  }
};

/**
 * Notify OTTO SEO of a route change
 * This function can be called from route change handlers
 */
export const notifyOttoRouteChange = (data: {
  path: string;
  title: string;
  url: string;
  description?: string;
}) => {
  try {
    // Skip if not in browser environment (SSR)
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    
    // If description is provided, update meta tags
    if (data.description) {
      updateMetaTags({
        title: data.title,
        description: data.description
      });
    }
    
    // Trigger a custom event for OTTO to detect
    const ottoEvent = new CustomEvent('ottoRouteChange', {
      detail: data
    });
    document.dispatchEvent(ottoEvent);
    
    // Push to dataLayer for Google Analytics integration
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'virtualPageview',
        'pageTitle': data.title,
        'pagePath': data.path,
        'pageUrl': data.url,
        'pageDescription': data.description || document.querySelector('meta[name="description"]')?.getAttribute('content') || ''
      });
    }
    
    // Let SearchAtlas know about the route change (if their script is loaded)
    if (window.sa_event) {
      window.sa_event('routeChange', data);
    }
    
    console.log('OTTO SEO: Route change notified', data.path);
  } catch (error) {
    console.warn('Error notifying OTTO of route change:', error);
  }
};

/**
 * Format a page title according to SEO best practices
 * Following the pattern: [Service/Topic] | [Location] | [Brand] 
 */
export const formatPageTitle = (title: string, includeLocation = true): string => {
  // If title already has a pipe, assume it's already formatted
  if (title.includes('|')) return title;
  
  const locationPart = includeLocation ? 'Baton Rouge, LA' : '';
  const brandPart = 'Astrem';
  
  // Format: Service/Topic | Location | Brand
  let formattedTitle = title.trim();
  if (includeLocation) formattedTitle += ` | ${locationPart}`;
  formattedTitle += ` | ${brandPart}`;
  
  return formattedTitle;
};

/**
 * Format a meta description according to SEO best practices
 * Following the pattern: [Service/Topic description] in [Location]. [Brand positioning]. [Call to action]
 */
export const formatMetaDescription = (description: string, customCta?: string): string => {
  // If description is already long and well-formed, return it as is
  if (description.length > 120 && (description.includes('Baton Rouge') || description.includes('LA'))) {
    return description;
  }
  
  const location = 'Baton Rouge, LA';
  const brandPositioning = 'Astrem delivers professional solutions';
  const defaultCta = 'Contact us today for a free consultation!';
  
  let formattedDescription = description.trim();
  
  // Only add location if not already present
  if (!formattedDescription.includes('Baton Rouge') && !formattedDescription.includes('LA')) {
    formattedDescription += ` in ${location}`;
  }
  
  // Only add brand positioning if not already present
  if (!formattedDescription.includes('Astrem')) {
    formattedDescription += `. ${brandPositioning}`;
  }
  
  // Add call to action if not already present
  if (!formattedDescription.includes('contact') && !formattedDescription.includes('Call')) {
    formattedDescription += `. ${customCta || defaultCta}`;
  }
  
  return formattedDescription;
};

/**
 * Update meta tags directly in the DOM
 * Enhanced to be SSR-friendly by checking for window/document existence
 */
export const updateMetaTags = (data: {
  title: string;
  description: string;
  type?: string;
  imageUrl?: string;
  canonicalUrl?: string;
}) => {
  // Format title and description if they don't follow our pattern
  let title = data.title;
  if (!title.includes('|') && !title.includes('Astrem')) {
    title = formatPageTitle(title);
  }
  
  let description = data.description;
  if (!description.includes('Baton Rouge') || !description.includes('contact')) {
    description = formatMetaDescription(description);
  }
  
  // Ensure description is not empty
  if (!description || description.trim() === '') {
    console.warn('OTTO SEO: Empty description provided, using fallback');
    description = "Professional websites, content & marketing solutions by Astrem in Baton Rouge, LA. Transform your digital presence - contact us today!";
  }
  
  // Check if we're in a browser environment - this makes the function SSR-safe
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Set title
    document.title = title;
    
    // Helper function to update or create meta tags - ensure they exist
    const updateMetaTag = (selector: string, content: string) => {
      let tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        // Create the tag if it doesn't exist
        tag = document.createElement('meta');
        if (selector.includes('property=')) {
          tag.setAttribute('property', selector.split('property="')[1].split('"')[0]);
        } else if (selector.includes('name=')) {
          tag.setAttribute('name', selector.split('name="')[1].split('"')[0]);
        }
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    };
    
    // Update basic meta tags - ensure description is present!
    updateMetaTag('meta[name="description"]', description);
    
    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', title);
    updateMetaTag('meta[property="og:description"]', description);
    if (data.type) {
      updateMetaTag('meta[property="og:type"]', data.type);
    }
    if (data.imageUrl) {
      updateMetaTag('meta[property="og:image"]', data.imageUrl);
      updateMetaTag('meta[name="twitter:image"]', data.imageUrl);
    }
    
    // Update canonical URL if provided
    if (data.canonicalUrl) {
      const canonicalTag = document.querySelector('link[rel="canonical"]');
      if (canonicalTag) {
        canonicalTag.setAttribute('href', data.canonicalUrl);
      } else {
        const newCanonicalTag = document.createElement('link');
        newCanonicalTag.setAttribute('rel', 'canonical');
        newCanonicalTag.setAttribute('href', data.canonicalUrl);
        document.head.appendChild(newCanonicalTag);
      }
    }
    
    console.log('OTTO SEO: Meta tags updated', {title, description});
  }
  
  return { title, description }; // Return values for SSR use
};

// Type declaration for window.sa_event
declare global {
  interface Window {
    sa_event?: (eventName: string, data: any) => void;
    dataLayer: any[];
  }
}

export default {
  notifyOttoContentUpdate,
  notifyOttoRouteChange,
  updateMetaTags,
  formatPageTitle,
  formatMetaDescription
};
